import { AfterViewInit, Component, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { Subscription, switchMap } from 'rxjs';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { AuthenticationService } from '../../../services/authentication.service';
import { UserConfigsService } from '../../../services/user-configs.service';
import { UserConfigs } from '../../../models/user-configs.model';
import { CasesService } from '../../../services/cases.service';
import { LexbeCase } from '../../../models/case.model';
import { HelpService } from '../../../services/help.service';
import { LoginRouteService } from '../../../services/login-route.service';
import { OkCancelDialogComponent } from '../../dialogs/ok-cancel/ok-cancel-dialog.component';
import { HideShowService } from '../../../services/hide-show.service';
import { CaseManager, ProcessingStepCount } from '../../../managers/case.manager';
import { Router } from '@angular/router';
import { UserNotificationManager } from '../../../managers/user-notification.manager';
import { map, take } from 'rxjs/operators';
import { NotificationHubSdk } from 'src/app/sdks/notification.hub-sdk';
import { DocViewerStore } from '@store/legacy/doc-viewer.store';
import { LexbeThemeService } from '@services/lexbe-theme.service';
import { Store } from "@ngrx/store";
import { selectIsProcessing, selectSteps } from "@store/global/global.selectors";

@Component({
    selector: 'lex-right-menu',
    templateUrl: './right-menu.component.html',
    styleUrls: ['./right-menu.component.scss'],
    standalone: false
})
export class RightMenuComponent implements OnInit, AfterViewInit, OnDestroy {
	public helpLink = 'https://sites.google.com/a/ediscoveryplatform.com/help/home/help';
	public supportLink = 'https://www.lexbe.com/support/';
	public currentCaseName = 'More...';
	public currentCaseTooltip = '';
	public isProcessing = false;
	public shouldNotify = false;

	public currentCase: LexbeCase;
	public processingSteps: ProcessingStepCount[];

	public isDarkTheme: boolean = false;

	private subscription = new Subscription();

	constructor(
		public hideShowService: HideShowService,
		private loginRouteService: LoginRouteService,
		private authenticationService: AuthenticationService,
		private userService: UserConfigsService,
		private casesService: CasesService,
		private helpService: HelpService,
		private notificationHubSdk: NotificationHubSdk,
		private dialog: MatDialog,
		private caseManager: CaseManager,
		private router: Router,
		private userNotificationManager: UserNotificationManager,
		private auth: AuthenticationService,
		private docViewerStore: DocViewerStore,
		private renderer: Renderer2,
		private themeService: LexbeThemeService,
		private store: Store) {

		this.setTheme(this.themeService.isDarkMode());
	}

	ngOnInit(): void {
		this.userNotificationManager.shouldNotify$.subscribe(s => this.shouldNotify = s);

		// todo: Remove after new login page is created.
		this.authenticationService.getToken$().pipe(take(1)).subscribe(() => {
			this.userNotificationManager.refresh();
		});

		this.subscription.add(this.store.select(selectIsProcessing)
			.subscribe((isProcessing) => {
				this.isProcessing = isProcessing;
				this.userNotificationManager.refresh();
			}));

		// This gets initial case name.
		this.subscription.add(this.userService.getUserConfigs().subscribe((userConfigs: UserConfigs) => {
			if (userConfigs?.currentCase) {
				this.showCaseName(userConfigs.currentCase.caseName);
				this.currentCase = userConfigs.currentCase;
			}
		}));

		// This keeps case name updated whenever current case changes anywhere in the app.
		this.subscription.add(this.casesService.listenCurrentCase().subscribe((currentCase: LexbeCase) => {
			this.showCaseName(currentCase.caseName);
		}));

		// This updates the help URL from any page.
		this.subscription.add(this.helpService.listenHelpLinkUrl().subscribe((url: string) => {
			setTimeout(() => {
				this.helpLink = url;
			}, 0);
		}));

		this.subscription.add(this.store.select(selectSteps).pipe(
			map(steps =>
				steps?.filter(step =>
					!step.workflowExecutionState || step.workflowExecutionState !== 'OnHold'
				)
			)
		).subscribe((psc) => {
			this.processingSteps = psc || [];
			return this.processingSteps;
		}));
	}

	ngAfterViewInit() {
		this.themeService.isDarkMode$.subscribe((value) => {
			this.setTheme(value);
		});
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	public notifyOnProcessingComplete($event) {
		const isChecked = $event.checked;
		if (!isChecked) {
			this.toggleNotification();
			return;
		}

		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.data = {
			title: 'Processing Complete Email Confirmation',
			message: 'Send email notification when processing is done?'
		};

		this.dialog.open(OkCancelDialogComponent, dialogConfig).afterClosed().subscribe((okClicked: boolean) => {
			if (okClicked) {
				this.toggleNotification();
			} else {
				$event.source.checked = false;
			}
		});
	}

	private toggleNotification() {
		this.notificationHubSdk.toggleNotifyWhenCaseCompletesProcessing().subscribe(() => {
			this.userNotificationManager.refresh();
		});
	}

	public getLogoutText(): string {
		return this.authenticationService.isLoggedIn() ? 'Sign out' : 'Sign in';
	}

	public logout(): void {
		this.authenticationService.logout().pipe(
			switchMap(() => {
				localStorage.removeItem('SearchItemList');
				localStorage.removeItem('SearchQuery');
				localStorage.removeItem('ContextMapping');
				return this.loginRouteService.redirectToLogin()
		})).subscribe();
	}

	public formatNumber(num: number): string {
		return num.toLocaleString();
	}

	public hideShow(): void {
		this.hideShowService.toggle();
		this.docViewerStore.updateState((currentState) => {
			currentState.rightPanelState = this.hideShowService.show ? 'expanded' : 'collapsed';
			return currentState;
		});
	}

	public isSectionActive(route: string) {
		return this.router.isActive(route, {
			paths: 'subset',
			queryParams: 'subset',
			fragment: 'ignored',
			matrixParams: 'ignored'
		});
	}

	public toggleTheme($event) {
		this.themeService.setLocalStorage($event.checked);
		this.setTheme($event.checked);
	}

	public setTheme(isDarkMode: boolean) {
		this.isDarkTheme = isDarkMode;

		if (this.isDarkTheme) {
			this.renderer.addClass(document.body, 'dark-theme');
		}
		else {
			this.renderer.removeClass(document.body, 'dark-theme');
		}
	}

	private showCaseName(caseName: string): void {
		if (!caseName) {
			caseName = 'More...';
			this.currentCaseTooltip = '';
		} else {
			this.currentCaseTooltip = caseName;
			caseName = (caseName.length > 100) ? caseName.substring(0, 100) : caseName;
		}
		this.currentCaseName = caseName;
	}
}
